import { PriceService } from '../modules/price-service.js';
import { Logger } from '../../config/logger.js';
import { SettingsService } from '../modules/settings-service.js';

/**
 * GlobalAudioWatcher
 * Plays notification sounds for new prices when the user is NOT on an active pricing grid tab.
 * Uses a debounce timer to prevent sound overlap.
 */
export const GlobalAudioWatcher = {
    initialized: false,
    watcherReady: false,
    knownPrices: new Set(),
    lastAudioTime: 0,
    unsubscribe: null,

    init(userFirm, userRole) {
        if (this.initialized) return;
        this.initialized = true;

        const path = window.location.pathname;
        const isPricesPage = path.includes('swaps_prices.html') || 
                             path.includes('swaption_prices.html') || 
                             path.includes('spreads.html') || 
                             path === '/';

        if (isPricesPage) {
            // Do not run the global audio watcher on price grid pages,
            // as GridRenderer handles audio locally with contextual mid-logic there.
            return;
        }

        Logger.log('[GlobalAudioWatcher] Activating background price sound alerts');

        this.unsubscribe = PriceService.listenForLivePrices((pricesByVertex) => {
            const settings = SettingsService.get();
            const debounceMs = settings.audioDebounceMs || 1000;

            if (!this.watcherReady) {
                // Initial load: populate cache without playing sounds
                for (const vertex in pricesByVertex) {
                    pricesByVertex[vertex].forEach(p => this.knownPrices.add(p._docId + '_' + p.price));
                }
                this.watcherReady = true;
                return;
            }

            let shouldBeep = false;

            for (const vertex in pricesByVertex) {
                pricesByVertex[vertex].forEach(p => {
                    if (!p.valid) return;
                    
                    const key = p._docId + '_' + p.price;
                    if (!this.knownPrices.has(key)) {
                        this.knownPrices.add(key);
                        
                        // If it's a new price and NOT from the current user
                        if (p.ownerId !== userFirm) {
                            shouldBeep = true;
                        }
                    }
                });
            }

            if (shouldBeep) {
                const now = Date.now();
                if (now - this.lastAudioTime > debounceMs) {
                    this.playGlobalSound(settings);
                    this.lastAudioTime = now;
                }
            }

        }, userFirm, userRole);
    },

    playGlobalSound(settings) {
        // Fallback cascade: NewPrice -> BetweenIndics -> General Alert
        const soundToPlay = [settings.audioNewPrice, settings.audioBetweenIndics, settings.audioAlertType].find(s => s && s !== 'none') || 'none';
        
        if (soundToPlay !== 'none') {
            const audio = new Audio(`/assets/sounds/${soundToPlay}.mp3`);
            audio.volume = 0.5;
            audio.play().catch(e => {
                Logger.warn("[GlobalAudioWatcher] Audio play blocked by browser policies", e);
            });
        }
    }
};
